<script lang="ts">
	import type { ISceneObj } from '../../../types/SceneType';
	import { buildSceneSemanticPreview } from '../helpers/sceneSemanticPreview';

	export let scene: ISceneObj | null = null;
	export let onClose: () => void = () => {};

	type Tab = 'preview' | 'full' | 'conditions' | 'actions';
	let activeTab: Tab = 'preview';
	let copied = false;

	$: fullJson = scene ? JSON.stringify(scene, null, 2) : '';
	$: conditionsJson = scene ? JSON.stringify({ if: scene.if }, null, 2) : '';
	$: actionsJson = scene ? JSON.stringify({ do: scene.do, else: scene.else }, null, 2) : '';
	$: semanticPreview = scene ? buildSceneSemanticPreview(scene) : '';

	$: displayJson =
		activeTab === 'preview'
			? semanticPreview
			: activeTab === 'full'
				? fullJson
				: activeTab === 'conditions'
					? conditionsJson
					: actionsJson;

	async function handleCopy() {
		try {
			await navigator.clipboard.writeText(displayJson);
			copied = true;
			setTimeout(() => (copied = false), 1800);
		} catch {
			// Clipboard not available — silent fail
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (e.key === 'Escape') onClose();
	}
</script>

<svelte:window on:keydown={handleKeyDown} />

<div class="overlay" role="presentation" on:click|self={onClose}>
	<div class="viewer">
		<div class="viewer-header">
			<span class="viewer-title">
				<i class="fa fa-code"></i>
				Scene JSON
			</span>
			<button class="icon-btn" on:click={onClose} aria-label="Close">
				<i class="fa fa-times"></i>
			</button>
		</div>

		<div class="tab-bar">
			<button
				class="tab"
				class:active={activeTab === 'preview'}
				on:click={() => (activeTab = 'preview')}
			>
				Semantics
			</button>
			<button
				class="tab"
				class:active={activeTab === 'full'}
				on:click={() => (activeTab = 'full')}
			>
				Full
			</button>
			<button
				class="tab"
				class:active={activeTab === 'conditions'}
				on:click={() => (activeTab = 'conditions')}
			>
				Conditions (if)
			</button>
			<button
				class="tab"
				class:active={activeTab === 'actions'}
				on:click={() => (activeTab = 'actions')}
			>
				Actions (do / else)
			</button>

			<div class="tab-spacer"></div>

			<button
				class="copy-btn"
				class:success={copied}
				on:click={handleCopy}
				aria-label="Copy JSON to clipboard"
			>
				<i class="fa {copied ? 'fa-check' : 'fa-copy'}"></i>
				{copied ? 'Copied' : 'Copy'}
			</button>
		</div>

		<div class="code-area">
			{#if displayJson}
				<pre>{displayJson}</pre>
			{:else}
				<p class="empty-msg">No scene loaded.</p>
			{/if}
		</div>
	</div>
</div>

<style>
	.overlay {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.55);
		z-index: 1000;
		display: flex;
		align-items: flex-start;
		justify-content: flex-end;
		padding: 1rem;
	}

	.viewer {
		width: min(560px, 94vw);
		max-height: calc(100vh - 2rem);
		background: #1a1a1a;
		border: 1px solid #333;
		border-radius: 10px;
		display: flex;
		flex-direction: column;
		box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
		overflow: hidden;
	}

	.viewer-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.75rem 1rem;
		border-bottom: 1px solid #2d2d2d;
		flex-shrink: 0;
	}

	.viewer-title {
		font-size: 0.9rem;
		font-weight: 600;
		color: #e5e7eb;
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.viewer-title i {
		color: #6366f1;
	}

	.icon-btn {
		background: transparent;
		border: none;
		color: #9ca3af;
		cursor: pointer;
		padding: 4px 6px;
		border-radius: 4px;
		line-height: 1;
	}

	.icon-btn:hover {
		background: rgba(255, 255, 255, 0.08);
		color: #e5e7eb;
	}

	.tab-bar {
		display: flex;
		align-items: center;
		gap: 2px;
		padding: 0.4rem 0.75rem;
		border-bottom: 1px solid #2d2d2d;
		flex-shrink: 0;
	}

	.tab {
		background: transparent;
		border: none;
		color: #9ca3af;
		cursor: pointer;
		padding: 4px 10px;
		border-radius: 5px;
		font-size: 0.78rem;
		font-weight: 500;
		transition: all 0.15s;
	}

	.tab:hover {
		background: rgba(255, 255, 255, 0.06);
		color: #e5e7eb;
	}

	.tab.active {
		background: rgba(99, 102, 241, 0.18);
		color: #a5b4fc;
		border: 1px solid rgba(99, 102, 241, 0.3);
	}

	.tab-spacer {
		flex: 1;
	}

	.copy-btn {
		display: flex;
		align-items: center;
		gap: 5px;
		background: rgba(255, 255, 255, 0.06);
		border: 1px solid #3a3a3a;
		color: #9ca3af;
		cursor: pointer;
		padding: 4px 10px;
		border-radius: 5px;
		font-size: 0.78rem;
		font-weight: 500;
		transition: all 0.15s;
	}

	.copy-btn:hover {
		background: rgba(255, 255, 255, 0.1);
		color: #e5e7eb;
	}

	.copy-btn.success {
		background: rgba(16, 185, 129, 0.14);
		border-color: rgba(16, 185, 129, 0.4);
		color: #6ee7b7;
	}

	.code-area {
		flex: 1;
		overflow-y: auto;
		padding: 0;
	}

	pre {
		margin: 0;
		padding: 1rem;
		font-family: 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
		font-size: 0.78rem;
		line-height: 1.65;
		color: #d1fae5;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.empty-msg {
		padding: 2rem;
		color: #6b7280;
		font-size: 0.85rem;
		text-align: center;
	}
</style>
